/**
 * @file CoursesSection.tsx
 * @summary Section page d'accueil — CoursesSection
 *
 * Bloc de contenu affiché sur la home (`/`). Composant présentationnel utilisant les tokens du design system (Charbon/Or, Bebas Neue/Inter).
 *
 * @see docs/07-CODEBASE.md — conventions de code
 * @see docs/02-FRONTEND.md — design system (tokens HSL, typographie)
 */
import Section from "@/components/Section";
import { Users, Dumbbell, Baby, HeartPulse, Target, Clock, Zap, Trophy, Star, Crown, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import iconReprendreSport from "@/assets/icon-reprendre-sport.webp";
import iconSante from "@/assets/icon-sante.webp";
import iconPoids from "@/assets/icon-poids.webp";
import iconDouleurs from "@/assets/icon-douleurs.webp";
import iconEnergie from "@/assets/icon-energie.webp";
import iconMuscler from "@/assets/icon-muscler.webp";
import iconCompetition from "@/assets/icon-competition.webp";
import iconDiversifier from "@/assets/icon-diversifier.webp";
import iconJobSportif from "@/assets/icon-job-sportif.webp";

const objectives = [
  { icon: iconSante, label: "Reprendre le sport / Améliorer ma santé" },
  { icon: iconPoids, label: "Perdre du poids" },
  { icon: iconDouleurs, label: "Limiter mes douleurs" },
  { icon: iconEnergie, label: "Retrouver de l'énergie ou de la motivation" },
  { icon: iconMuscler, label: "Me muscler" },
  { icon: iconCompetition, label: "Préparer une échéance sportive (marathon, Hyrox, triathlon, compétition, ...)" },
  { icon: iconJobSportif, label: "Maintenir un bon niveau sportif pour mon job (forces de l'ordre, pompier, militaire, ...)" },
  { icon: iconDiversifier, label: "Diversifier mes activités sportives" },
];

const courses = [
  { icon: Users, title: "WOD", badge: "Max 10-12 pers.", desc: "Cours encadrés par un coach diplômé, adapté à tous, selon la méthode CrossFit" },
  { icon: Dumbbell, title: "Open Gym / Free", badge: null, desc: "Entraîne-toi en toute autonomie et profite d'un accès libre à la box" },
  { icon: Target, title: "Hybrid Training", badge: "Cours spécifique", desc: "WOD long et cardio (ergo, course à pied) avec des exercices de renforcement musculaire" },
  { icon: Baby, title: "Kids", badge: "Samedi", desc: "Séances adaptées pour les jeunes\nDécouverte du CrossFit dans un cadre ludique et sécurisé" },
  { icon: HeartPulse, title: "Cours Seniors", badge: "Jeudi 10h15-11h15", desc: "Reprends le sport en douceur\nMouvements fonctionnels adaptés, pour retrouver forme et mobilité" },
  { icon: Dumbbell, title: "Renforcement haltéro/gym", badge: "Créneaux variables indiqués par les coachs", desc: "Cours de renforcement spécifique haltérophilie et gymnastique, encadré par les coachs" },
];

const levels = [
  { name: "Foundations", desc: "Découverte et apprentissage des bases", step: 1, icon: Shield, color: "text-success border-success/30 bg-success/10" },
  { name: "Scaled", desc: "Mouvements et charges adaptés", step: 2, icon: Zap, color: "text-gold border-gold/30 bg-gold/10" },
  { name: "Inter", desc: "Progression vers les standards", step: 3, icon: Star, color: "text-gold border-gold/40 bg-gold/15" },
  { name: "RX", desc: "Standards / compétiteurs", step: 4, icon: Trophy, color: "text-cta border-cta/30 bg-cta/10" },
  { name: "Elite", desc: "Le haut niveau !", step: 5, icon: Crown, color: "text-foreground border-charcoal/40 bg-charcoal/10" },
];

type SlotType = "WOD" | "FREE" | "HT" | "SENIOR" | "KIDS" | null;

const slotStyles: Record<string, string> = {
  WOD: "bg-gold/20 text-gold font-semibold",
  FREE: "bg-muted text-muted-foreground",
  HT: "bg-cta/15 text-cta font-semibold",
  SENIOR: "bg-success/15 text-success font-semibold",
  KIDS: "bg-kids/15 text-kids font-semibold",
};

const slotLabels: Record<string, string> = {
  WOD: "WOD",
  FREE: "Free",
  HT: "Hybrid Training",
  SENIOR: "Senior",
  KIDS: "Kids",
};

const days = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi"];
const hours = ["7h-8h", "8h-9h", "9h-10h", "10h-11h", "11h-12h", "12h15-13h15", "13h15-16h30", "16h30-17h30", "17h30-18h30", "18h30-19h30", "19h30-20h30"];

const scheduleData: (SlotType)[][] = [
  // Lundi
  ["WOD", "FREE", "WOD", "FREE", "FREE", "WOD", "FREE", "WOD", "WOD", "WOD", "HT"],
  // Mardi
  [null, null, "WOD", "FREE", "FREE", "HT", "FREE", "FREE", "WOD", "WOD", "WOD"],
  // Mercredi
  ["WOD", "FREE", "HT", "FREE", "FREE", "WOD", "FREE", "FREE", "WOD", "WOD", "WOD"],
  // Jeudi
  [null, null, "WOD", "SENIOR", "FREE", "WOD", "FREE", "WOD", "HT", "WOD", "WOD"],
  // Vendredi
  ["WOD", "FREE", "WOD", "FREE", "FREE", "WOD", "FREE", "FREE", "WOD", "WOD", null],
  // Samedi
  [null, null, "KIDS", "WOD", "WOD", null, null, null, null, null, null],
];

export default function CoursesSection() {
  const scrollTo = () => document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" });

  return (
    <Section id="cours" className="bg-card">
      <div className="text-center mb-12">
        <span className="inline-block text-sm font-semibold text-gold tracking-wider mb-2 font-body normal-case">Pour tous les niveaux</span>
        <h2 className="text-4xl md:text-5xl text-foreground">Nos cours</h2>
      </div>

      {/* Course cards */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-20">
        {courses.map((c) => (
          <div key={c.title} className="bg-charcoal text-charcoal-foreground border border-charcoal rounded-lg p-6 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 group">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-gold/10 flex items-center justify-center group-hover:bg-gold/20 transition-colors">
                <c.icon className="text-gold" size={22} />
              </div>
              <div>
                <h3 className="text-xl text-charcoal-foreground">{c.title}</h3>
                {c.badge && <span className="text-xs font-semibold text-gold font-body normal-case">{c.badge}</span>}
              </div>
            </div>
            <p className="text-sm text-charcoal-foreground/70 font-body normal-case leading-relaxed whitespace-pre-line">{c.desc}</p>
          </div>
        ))}
      </div>

      {/* Planning */}
      <div className="bg-background border border-border rounded-lg p-6 mb-4 overflow-x-auto">
        <div className="flex items-center gap-3 mb-1">
          <Clock className="text-gold" size={28} />
          <h3 className="text-4xl md:text-5xl text-foreground">Planning</h3>
        </div>
        <p className="text-sm text-muted-foreground font-body normal-case mb-6">🗓 Nous sommes ouverts toute l'année*</p>

        <table className="w-full min-w-[700px] text-xs font-body normal-case">
          <thead>
            <tr>
              <th className="text-left p-2 text-muted-foreground font-semibold">Horaires</th>
              {days.map((d) => (
                <th key={d} className="p-1.5 text-center text-foreground font-semibold">{d}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {hours.map((hour, hi) => (
              <tr key={hour} className="border-t border-border">
                <td className="p-2 font-semibold text-muted-foreground whitespace-nowrap">{hour}</td>
                {days.map((_, di) => {
                  const slot = scheduleData[di]?.[hi] ?? null;
                  const isSenior = slot === "SENIOR";
                  return (
                    <td key={di} className="p-1.5 text-center">
                      {slot ? (
                        <div>
                          <span className={`inline-block w-full py-1 rounded text-[11px] ${slotStyles[slot]}`}>
                            {slotLabels[slot]}
                          </span>
                          {isSenior && (
                            <span className="block text-[9px] text-success/70 mt-0.5 font-body normal-case">10h15-11h15</span>
                          )}
                        </div>
                      ) : (
                        <span className="text-muted-foreground" aria-hidden="true">—</span>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Definitions — inline compact */}
      <div className="flex flex-wrap justify-center gap-x-6 gap-y-1 mb-4 text-xs font-body normal-case text-muted-foreground">
        <span><span className="font-semibold text-gold">WOD</span> = Workout of the Day, cours collectif coaché (max 12)</span>
        <span><span className="font-semibold text-foreground">Free</span> = Séance libre hors cours encadrés</span>
        <span><span className="font-semibold text-cta">Hybrid Training</span> = cardio long & renforcement musculaire</span>
        <span><span className="font-semibold text-kids">Kids</span> = 6–10 ans</span>
        <span><span className="font-semibold text-success">Senior</span> = 60–99 ans</span>
      </div>

      {/* Resawod note */}
      <p className="text-center text-[11px] text-muted-foreground font-body normal-case mb-2">
        L'inscription à un créneau horaire, hors premiers cours d'essai, s'effectue via l'application Resawod et sous réserve de places disponibles
      </p>
      <p className="text-center text-[11px] text-muted-foreground font-body normal-case mb-20">
        * La box est ouverte les jours fériés le matin à l'exception du 1er janvier, 1er mai et 25 décembre pendant lesquels la box est fermée toute la journée
      </p>

      {/* Session flow — dark background */}
      <div className="bg-charcoal text-charcoal-foreground rounded-lg p-8 mb-20">
        <h3 className="text-2xl text-gold mb-6 text-center">Déroulé d'une séance</h3>
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { step: "01", title: "Échauffement", desc: "Mobilité et échauffement musculaire pour préparer le corps" },
            { step: "02", title: "Skill / Renforcement", desc: "Travail technique sur un mouvement ou une série pour progresser" },
            { step: "03", title: "WOD", desc: "Le moment où on se dépasse — force, cardio, plaisir. Tous ensemble !" },
          ].map((s) => (
            <div key={s.step} className="text-center">
              <span className="text-3xl font-heading text-gold">{s.step}</span>
              <h4 className="text-lg text-charcoal-foreground mt-1">{s.title}</h4>
              <p className="text-sm text-charcoal-foreground/70 mt-2 font-body normal-case">{s.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Levels — Responsive redesign */}
      <div className="text-center mb-8">
        <h3 className="text-2xl text-foreground">Cours adaptés à tous les niveaux...</h3>
        <p className="text-muted-foreground mt-2 font-body normal-case text-sm">Débutant, blessé, enceinte, en reprise, compétiteur… il y a une place pour toi</p>
      </div>
      <div className="max-w-4xl mx-auto mb-8">
        {/* Desktop: horizontal */}
        <div className="hidden sm:flex gap-2 items-stretch">
          {levels.map((l) => (
            <div
              key={l.name}
              className={`flex-1 ${l.color} border rounded-lg p-4 text-center transition-all duration-300 hover:-translate-y-1 hover:shadow-md`}
            >
              <l.icon size={24} className="mx-auto mb-2" />
              <p className="text-sm font-bold font-body">{l.name}</p>
              <p className="text-[11px] text-muted-foreground mt-1 font-body normal-case leading-tight">{l.desc}</p>
            </div>
          ))}
        </div>
        {/* Mobile: vertical stack */}
        <div className="sm:hidden space-y-2">
          {levels.map((l) => (
            <div
              key={l.name}
              className={`flex items-center gap-3 ${l.color} border rounded-lg px-4 py-3`}
            >
              <l.icon size={20} className="shrink-0" />
              <div>
                <p className="text-sm font-bold font-body">{l.name}</p>
                <p className="text-[11px] text-muted-foreground font-body normal-case">{l.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Objectives section */}
      <div className="text-center mt-16 mb-8">
        <h3 className="text-2xl text-foreground">... et à tous les objectifs</h3>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 max-w-4xl mx-auto mb-12">
        {objectives.map((obj) => (
          <div key={obj.label} className="flex flex-col items-center text-center gap-3">
            <div className="w-16 h-16 rounded-full overflow-hidden bg-charcoal flex items-center justify-center">
              <img src={obj.icon} alt={obj.label} className="w-full h-full object-cover" loading="lazy" decoding="async" />
            </div>
            <p className="text-sm font-body normal-case text-foreground leading-tight">{obj.label}</p>
          </div>
        ))}
      </div>

      <div className="text-center">
        <Button onClick={scrollTo} className="bg-cta text-cta-foreground hover:bg-cta/90 rounded-lg font-semibold px-8">
          Je réserve mon essai gratuit
        </Button>
      </div>
    </Section>
  );
}
